/**
 * TipTapEditor (T022, T022b - US2)
 * WYSIWYG editor wrapper using TipTap with all required extensions
 * Includes paste URL auto-embed for YouTube, Vimeo, and Spotify
 */

import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { IframeExtension } from './IframeExtension';
import { FontSize, FontFamily } from './FontSizeExtension';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const lowlight = createLowlight(common);

const EMBED_PATTERNS: Array<{ regex: RegExp; toSrc: (match: RegExpMatchArray) => string }> = [
  {
    regex: /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([\w-]+)/,
    toSrc: (m) => `https://www.youtube.com/embed/${m[1]}`,
  },
  {
    regex: /(?:https?:\/\/)?youtu\.be\/([\w-]+)/,
    toSrc: (m) => `https://www.youtube.com/embed/${m[1]}`,
  },
  {
    regex: /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
    toSrc: (m) => `https://player.vimeo.com/video/${m[1]}`,
  },
  {
    regex: /(?:https?:\/\/)?open\.spotify\.com\/(track|album|playlist|episode)\/([\w]+)/,
    toSrc: (m) => `https://open.spotify.com/embed/${m[1]}/${m[2]}`,
  },
];

function detectEmbedUrl(text: string): string | null {
  for (const pattern of EMBED_PATTERNS) {
    const match = text.match(pattern.regex);
    if (match) return pattern.toSrc(match);
  }
  return null;
}

const pasteEmbedPluginKey = new PluginKey('pasteEmbed');

function createPasteEmbedPlugin(insertIframe: (src: string) => void) {
  return new Plugin({
    key: pasteEmbedPluginKey,
    props: {
      handlePaste(_view, event) {
        const text = event.clipboardData?.getData('text/plain')?.trim();
        if (!text) return false;
        const embedSrc = detectEmbedUrl(text);
        if (embedSrc) {
          insertIframe(embedSrc);
          return true;
        }
        return false;
      },
    },
  });
}

type TipTapEditorProps = {
  content: string;
  onChange: (html: string) => void;
  editable?: boolean;
  onEditorReady?: (editor: ReturnType<typeof useEditor>) => void;
};

export function TipTapEditor({ content, onChange, editable = true, onEditorReady }: TipTapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      Image.configure({ inline: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Color,
      TextStyle,
      CodeBlockLowlight.configure({ lowlight }),
      FontSize,
      FontFamily,
      IframeExtension,
    ],
    content,
    editable,
    editorProps: {
      attributes: { class: 'prose max-w-none min-h-[300px] p-4 focus:outline-none' },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;

    onEditorReady?.(editor);

    const insertIframe = (src: string) => {
      editor.chain().focus().insertContent({ type: 'iframe', attrs: { src } }).run();
    };

    const plugin = createPasteEmbedPlugin(insertIframe);
    editor.registerPlugin(plugin);

    return () => {
      editor.unregisterPlugin(pasteEmbedPluginKey);
    };
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (editor && content && !editor.isDestroyed) {
      const current = editor.getHTML();
      if (current === '<p></p>' || current === '') {
        editor.commands.setContent(content);
      }
    }
  }, [editor, content]);

  return (
    <div data-testid="tiptap-editor" className="border border-gray-300 rounded-md overflow-hidden">
      <EditorContent editor={editor} />
    </div>
  );
}

export { detectEmbedUrl };
